import { http } from '@/interceptors/axiosRequestInterceptor';
import { FactoryNestingSettings, PRODUCTION_NESTING_KEY } from '@/models/FactoryNestingSettings';

/**
 * Factory settings endpoints (backend AdminFactoryController::getSettings /
 * updateSettings). Read requires the Dashboard or Factory permission; write
 * requires Factory — a 403 from save means the user may view orders but not
 * manage factories.
 *
 * DEV-ONLY fallback: while the backend endpoints aren't deployed to the
 * environment the dev server points at, a 404 falls back to localStorage so
 * nesting stays testable locally. `import.meta.env.DEV` is a compile-time
 * constant, so production builds contain none of this.
 */
export interface FactorySettingsApi {
  getNestingSettings: (factoryId: number) => Promise<FactoryNestingSettings | null>;
  saveNestingSettings: (
    factoryId: number,
    value: FactoryNestingSettings
  ) => Promise<FactoryNestingSettings | null>;
}

const DEV_FALLBACK_DEFAULTS: FactoryNestingSettings = {
  paper_width: 63, // inches (~160 cm roll)
  paper_length: 118, // inches (~300 cm)
  allowed_rotations: [90],
  piece_padding: 0.125, // inches
  cut_outline_width_mm: 1,
  search_outline: false,
  search_outline_offset_mm: 5,
  search_outline_width_mm: 1,
  label_order_no: false,
  label_player: false,
  label_panel: false,
};

function devFallbackKey(factoryId: number): string {
  // v2: units changed from cm to inches — a new key orphans stale cm values.
  return `dev_production_nesting_v2_${factoryId}`;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

export function useFactorySettings(): FactorySettingsApi {
  async function getNestingSettings(factoryId: number): Promise<FactoryNestingSettings | null> {
    try {
      const response = await http.get(`factory/${factoryId}/settings`, {
        params: { 'keys[]': PRODUCTION_NESTING_KEY },
      });
      return response.data?.result?.settings?.[PRODUCTION_NESTING_KEY] ?? null;
    } catch (error) {
      if (import.meta.env.DEV && isNotFound(error)) {
        console.info('[dev] factory settings endpoint not deployed — using localStorage fallback');
        try {
          const stored = localStorage.getItem(devFallbackKey(factoryId));
          return stored ? (JSON.parse(stored) as FactoryNestingSettings) : DEV_FALLBACK_DEFAULTS;
        } catch {
          return DEV_FALLBACK_DEFAULTS;
        }
      }
      throw error;
    }
  }

  async function saveNestingSettings(
    factoryId: number,
    value: FactoryNestingSettings
  ): Promise<FactoryNestingSettings | null> {
    try {
      const response = await http.put(`factory/${factoryId}/settings`, {
        settings: [{ key: PRODUCTION_NESTING_KEY, value }],
      });
      return response.data?.result?.settings?.[PRODUCTION_NESTING_KEY] ?? null;
    } catch (error) {
      if (import.meta.env.DEV && isNotFound(error)) {
        console.info('[dev] factory settings endpoint not deployed — saving to localStorage');
        try {
          localStorage.setItem(devFallbackKey(factoryId), JSON.stringify(value));
        } catch {
          /* storage may be unavailable; the value still applies to this run */
        }
        return value;
      }
      throw error;
    }
  }

  return { getNestingSettings, saveNestingSettings };
}
